/**
 * Encodes today's OfferPdfDocument geometry as a DocumentLayoutV1 so the
 * shipped "Standard-Layout" looks like the current PDF for instances that
 * opt into the template editor. Existing instances with no template row keep
 * the hardcoded JSX path (layout == null).
 *
 * Coordinates are absolute within each band (header y=0 at page top;
 * footer y=0 at the top of the footer band).
 */
import {
  DEFAULT_DOCUMENT_PAGE_MARGINS_PT,
  PDF_A4_WIDTH_PT,
  type DocumentLayoutV1,
  type LayoutElement,
  newLayoutElementId,
} from './documentLayoutTypes';

/** Page padding used by the legacy OfferPdfDocument. */
const PAGE_PAD_X = 40;
const PAGE_PAD_TOP = 36;
/** A4 width is fractional (595.28) — keep derived geometry on whole points. */
const CONTENT_WIDTH = Math.round(PDF_A4_WIDTH_PT - PAGE_PAD_X * 2); // 515
const RIGHT_EDGE = Math.round(PDF_A4_WIDTH_PT - PAGE_PAD_X); // 555

function el(
  key: LayoutElement['key'],
  partial: Omit<LayoutElement, 'id' | 'key'> & { id?: string },
): LayoutElement {
  return {
    id: partial.id ?? newLayoutElementId(key),
    key,
    ...partial,
  };
}

/**
 * Stable IDs for the default layout so round-trips stay predictable in tests.
 */
const IDS = {
  logo: 'default-logo',
  sender: 'default-sender',
  recipient: 'default-recipient',
  ansprech: 'default-ansprech',
  title: 'default-title',
  offerTitle: 'default-offer-title',
  leistungsort: 'default-leistungsort',
  leistungszeitraum: 'default-leistungszeitraum',
  issueDate: 'default-issue-date',
  legal: 'default-legal',
  contact: 'default-contact',
  bank: 'default-bank',
  pageNumber: 'default-page-number',
} as const;

/**
 * Vertical stack matching OfferPdfDocument flow (no overlaps):
 * logo (36–92) → sender → recipient → ansprechpartner → title → meta row.
 */
export function createDefaultDocumentLayout(): DocumentLayoutV1 {
  const logoTop = PAGE_PAD_TOP; // 36
  const logoH = 56;
  const senderTop = logoTop + logoH + 8; // 100
  const recipientTop = senderTop + 14; // 114
  const recipientH = 56;
  const ansprechTop = recipientTop + recipientH + 10; // 180
  const ansprechH = 56;
  const titleTop = ansprechTop + ansprechH + 12; // 248
  const titleH = 20;
  const offerTitleTop = titleTop + titleH + 4; // 272
  const offerTitleH = 20;
  const metaTop = offerTitleTop + offerTitleH + 10; // 302
  const metaH = 36;
  const headerHeight = metaTop + metaH + 12; // ~350

  const footerHeight = 84;

  const headerElements: LayoutElement[] = [
    el('logo', {
      id: IDS.logo,
      xPt: RIGHT_EDGE - 160,
      yPt: logoTop,
      wPt: 160,
      hPt: logoH,
      locked: false,
    }),
    el('senderHeadline', {
      id: IDS.sender,
      xPt: PAGE_PAD_X,
      yPt: senderTop,
      wPt: Math.min(CONTENT_WIDTH, RIGHT_EDGE - 180),
      hPt: 12,
      fontSizePt: 7.5,
      bold: true,
      color: '#0f172a',
      locked: false,
    }),
    el('recipient', {
      id: IDS.recipient,
      xPt: PAGE_PAD_X,
      yPt: recipientTop,
      wPt: 260,
      hPt: recipientH,
      fontSizePt: 9.5,
      color: '#334155',
      locked: false,
    }),
    el('ansprechpartner', {
      id: IDS.ansprech,
      xPt: PAGE_PAD_X,
      yPt: ansprechTop,
      wPt: 260,
      hPt: ansprechH,
      fontSizePt: 9.5,
      color: '#334155',
      locked: false,
    }),
    el('documentTitle', {
      id: IDS.title,
      xPt: PAGE_PAD_X,
      yPt: titleTop,
      wPt: CONTENT_WIDTH,
      hPt: titleH,
      fontSizePt: 12.5,
      bold: true,
      color: '#0f172a',
      labelPlacement: 'inline',
      locked: false,
    }),
    el('offerTitle', {
      id: IDS.offerTitle,
      xPt: PAGE_PAD_X,
      yPt: offerTitleTop,
      wPt: CONTENT_WIDTH,
      hPt: offerTitleH,
      fontSizePt: 12.5,
      bold: true,
      color: '#0f172a',
      locked: false,
    }),
    el('leistungsort', {
      id: IDS.leistungsort,
      xPt: PAGE_PAD_X,
      yPt: metaTop,
      wPt: 200,
      hPt: metaH,
      fontSizePt: 9.5,
      color: '#0f172a',
      locked: false,
    }),
    el('leistungszeitraum', {
      id: IDS.leistungszeitraum,
      xPt: PAGE_PAD_X + 212,
      yPt: metaTop,
      wPt: 140,
      hPt: metaH,
      fontSizePt: 9.5,
      color: '#0f172a',
      locked: false,
    }),
    el('issueDate', {
      id: IDS.issueDate,
      xPt: RIGHT_EDGE - 140,
      yPt: metaTop,
      wPt: 140,
      hPt: metaH,
      align: 'right',
      fontSizePt: 9.5,
      color: '#0f172a',
      locked: false,
    }),
  ];

  const footerElements: LayoutElement[] = [
    el('legalBlock', {
      id: IDS.legal,
      xPt: PAGE_PAD_X,
      yPt: 8,
      wPt: 170,
      hPt: 58,
      fontSizePt: 7.5,
      color: '#334155',
      locked: false,
    }),
    el('contactBlock', {
      id: IDS.contact,
      xPt: PAGE_PAD_X + 175,
      yPt: 8,
      wPt: 170,
      hPt: 58,
      fontSizePt: 7.5,
      color: '#334155',
      locked: false,
    }),
    el('bankBlock', {
      id: IDS.bank,
      xPt: PAGE_PAD_X + 350,
      yPt: 8,
      wPt: 165,
      hPt: 58,
      fontSizePt: 7.5,
      color: '#334155',
      locked: false,
    }),
    el('pageNumber', {
      id: IDS.pageNumber,
      xPt: RIGHT_EDGE - 90,
      yPt: 68,
      wPt: 90,
      hPt: 12,
      align: 'right',
      fontSizePt: 8,
      color: '#64748b',
      locked: false,
    }),
  ];

  return {
    v: 1,
    header: { heightPt: headerHeight, elements: headerElements },
    footer: { heightPt: footerHeight, elements: footerElements },
    body: {
      accentColor: '#2563eb',
      tableHeaderBg: '#eceef1',
      fontSizePt: 9.5,
      pageMarginsPt: { ...DEFAULT_DOCUMENT_PAGE_MARGINS_PT },
      // Neue Vorlagen: Tabelle nicht verwaist unten auf Seite 1 beginnen lassen.
      tablePlacement: 'auto',
    },
  };
}

/** Deep-clone the default layout (fresh element IDs where callers want uniqueness). */
export function cloneDefaultDocumentLayout(opts?: { freshIds?: boolean }): DocumentLayoutV1 {
  const base = createDefaultDocumentLayout();
  if (!opts?.freshIds) {
    return structuredClone(base);
  }
  const clone = structuredClone(base);
  for (const elItem of clone.header.elements) {
    elItem.id = newLayoutElementId(elItem.key);
  }
  for (const elItem of clone.footer.elements) {
    elItem.id = newLayoutElementId(elItem.key);
  }
  return clone;
}
